import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import apiService from '../../../services/apiService';
import { AuthContext } from '../../../context/AuthContext';
import { FaPaperPlane, FaPaperclip, FaVideo, FaTimes, FaDownload, FaExpand } from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const socket = io(BASE_URL, { autoConnect: false });

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isImage = (mimeType = '') => mimeType.startsWith('image/');
const isPdf   = (mimeType = '') => mimeType === 'application/pdf';

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatLastMessageTime(date) {
  if (!date) return '';
  const now = new Date();
  const d   = new Date(date);
  return now.toDateString() === d.toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString();
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function AttachmentPreview({ attachments }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="flex flex-col gap-2 mt-1">
      {attachments.map((a, i) => (
        <div key={i}>
          {isImage(a.mimeType) ? (
            <>
              <img
                src={`${BASE_URL}${a.url}`}
                alt={a.originalName}
                className="max-w-[220px] rounded-xl cursor-pointer border border-gray-200 dark:border-gray-600 object-cover"
                style={{ maxHeight: 180 }}
                onClick={() => setLightbox(a)}
              />
              <p className="text-xs text-gray-400 mt-0.5">{a.originalName}</p>
            </>
          ) : (
            <a
              href={`${BASE_URL}${a.url}`}
              target="_blank"
              rel="noreferrer"
              download={a.originalName}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
            >
              <FaDownload size={13} />
              <span className="truncate max-w-[160px]">{a.originalName}</span>
              <span className="text-gray-400 text-xs ml-auto">{formatBytes(a.size)}</span>
            </a>
          )}
        </div>
      ))}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-white bg-black/50 rounded-full p-1.5 hover:bg-black/70"
              onClick={() => setLightbox(null)}
            >
              <FaTimes size={16} />
            </button>
            <img
              src={`${BASE_URL}${lightbox.url}`}
              alt={lightbox.originalName}
              className="rounded-xl max-h-[85vh] object-contain"
            />
            <p className="text-white text-xs text-center mt-2">{lightbox.originalName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ZoomCard({ zoomMeeting, isOwn }) {
  return (
    <div
      className={`rounded-2xl border p-4 mt-1 flex flex-col gap-2 min-w-[220px] ${
        isOwn
          ? 'bg-blue-700 border-blue-500 text-white'
          : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
      }`}
    >
      <div className="flex items-center gap-2 font-semibold text-sm">
        <FaVideo size={14} />
        {zoomMeeting.topic || 'Zoom Meeting'}
      </div>

      {zoomMeeting.password && (
        <p className={`text-xs ${isOwn ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>
          Passcode: <span className="font-mono font-semibold">{zoomMeeting.password}</span>
        </p>
      )}

      <a
        href={zoomMeeting.joinUrl}
        target="_blank"
        rel="noreferrer"
        className={`flex items-center justify-center gap-2 text-xs font-semibold py-2 px-4 rounded-xl transition ${
          isOwn
            ? 'bg-white text-blue-700 hover:bg-blue-50'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        <FaVideo size={12} /> Join Meeting
      </a>
    </div>
  );
}

function FilePickerPreview({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-4 pt-2">
      {files.map((f, i) => (
        <div
          key={i}
          className="relative flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-xl px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
        >
          {isImage(f.type) ? (
            <img
              src={URL.createObjectURL(f)}
              alt={f.name}
              className="w-8 h-8 rounded object-cover"
            />
          ) : (
            <FaDownload size={12} className="text-gray-400" />
          )}
          <span className="max-w-[100px] truncate text-xs">{f.name}</span>
          <button
            onClick={() => onRemove(i)}
            className="ml-1 text-gray-400 hover:text-red-500"
          >
            <FaTimes size={11} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function Chat() {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { consultationId }        = useParams();
  const navigate                  = useNavigate();

  const [participants,         setParticipants]         = useState([]);
  const [selectedParticipant,  setSelectedParticipant]  = useState(null);
  const [messages,             setMessages]             = useState([]);
  const [messageInput,         setMessageInput]         = useState('');
  const [loading,              setLoading]              = useState(false);
  const [chatLoading,          setChatLoading]          = useState(false);
  const [error,                setError]                = useState(null);
  const [pendingFiles,         setPendingFiles]         = useState([]);   // files chosen but not sent
  const [uploading,            setUploading]            = useState(false);
  const [creatingZoom,         setCreatingZoom]         = useState(false);

  const chatContainerRef = useRef(null);
  const fileInputRef     = useRef(null);

  // ── Socket connection lifecycle ──────────────────────────────────────────
  useEffect(() => {
    socket.on('connect',       ()  => console.log('Socket connected:', socket.id));
    socket.on('connect_error', (e) => { console.error(e); toast.error('Failed to connect to chat server'); });
    socket.on('disconnect',    (r) => console.log('Socket disconnected:', r));
    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
    };
  }, []);

  // ── Fetch participants ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchChatParticipants = async () => {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Session expired.'); navigate('/login'); return; }
      try {
        setLoading(true);
        let response;
        if (user?.role === 'specialist') {
          response = await apiService.get(`/chats/users/${user?.id || ''}`);
        } else {
          response = await apiService.get(`/chats/user/${user?.id || ''}`);
        }
        const data = Array.isArray(response) ? response : [response];
        setParticipants(data);
        if (consultationId) {
          const p = data.find((p) => p.consultationId === consultationId);
          if (p) setSelectedParticipant(p);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load chat participants');
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated() && user?.role) fetchChatParticipants();
  }, [user, consultationId, navigate, isAuthenticated]);

  // ── Fetch history + join socket room ────────────────────────────────────
  useEffect(() => {
    if (!consultationId || !selectedParticipant) return;
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Session expired.'); navigate('/login'); return; }

    const fetchMessages = async () => {
      try {
        setChatLoading(true);
        const response = await apiService.get(`/chats/${consultationId}`);
        setMessages(Array.isArray(response) ? response : []);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load messages');
      } finally {
        setChatLoading(false);
      }
    };

    fetchMessages();

    socket.auth = { token };
    socket.connect();
    socket.emit('joinChat', { consultationId, userId: user?.id });
    socket.on('receiveMessage', (msg) => setMessages((prev) => [...prev, msg]));

    return () => {
      socket.off('receiveMessage');
      socket.disconnect();
    };
  }, [consultationId, selectedParticipant, user, navigate]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Auth guard ───────────────────────────────────────────────────────────
  if (!isAuthenticated()) {
    toast.error('Please log in to access chat.');
    return <Navigate to="/login" />;
  }

  // ── Send text message ────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedParticipant) return;
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Session expired.'); navigate('/login'); return; }

    socket.emit('sendMessage', {
      consultationId,
      senderId:   user.id,
      receiverId: selectedParticipant.userId,
      content:    messageInput,
      modelType:  'Consultation',
    });
    setMessageInput('');
  };

  // ── Send files ───────────────────────────────────────────────────────────
  const sendFiles = async () => {
    if (!pendingFiles.length || !selectedParticipant) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('consultationId', consultationId);
      formData.append('modelType',      'Consultation');
      formData.append('senderId',       user.id);
      formData.append('receiverId',     String(selectedParticipant.userId));
      pendingFiles.forEach((f) => formData.append('files', f));

      await apiService.uploadFiles('/chats/upload', formData);
      // socket broadcast is done server-side; just clear the queue
      setPendingFiles([]);
    } catch (err) {
      toast.error('File upload failed: ' + String(err));
    } finally {
      setUploading(false);
    }
  };

  // ── Create Zoom meeting ──────────────────────────────────────────────────
  const createZoomMeeting = async () => {
    if (!selectedParticipant) return;
    setCreatingZoom(true);
    try {
      await apiService.post('/chats/zoom', {
        consultationId,
        modelType:  'Consultation',
        senderId:   user.id,
        receiverId: String(selectedParticipant.userId),
        topic:      `Consultation with ${selectedParticipant.fullName}`,
      });
      // socket broadcast is done server-side
    } catch (err) {
      toast.error('Could not create Zoom meeting: ' + String(err));
    } finally {
      setCreatingZoom(false);
    }
  };

  // ── File picker ──────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files);
    if (chosen.length + pendingFiles.length > 5) {
      toast.error('Maximum 5 files per message.');
      return;
    }
    const oversize = chosen.find((f) => f.size > 5 * 1024 * 1024);
    if (oversize) { toast.error(`${oversize.name} exceeds the 5 MB limit.`); return; }
    setPendingFiles((prev) => [...prev, ...chosen]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingFiles.length) sendFiles();
      else sendMessage();
    }
  };

  const handleParticipantSelect = (p) => {
    if (selectedParticipant?.consultationId !== p.consultationId) {
      setChatLoading(true);
      setSelectedParticipant(p);
      if (consultationId !== p.consultationId) {
        navigate(`/user/chat/${p.consultationId}`, { replace: true });
      }
    }
  };

  // ── Render a single message bubble ───────────────────────────────────────
  const renderMessage = (msg, index) => {
    const isOwn = msg.senderId?._id === user.id || msg.senderId === user.id;

    return (
      <div key={index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
        <div
          className={`flex items-start gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}
        >
          {/* Avatar */}
          <img
            src={
              msg.senderId?.profilePicture
                ? `${BASE_URL}${msg.senderId.profilePicture}`
                : 'https://via.placeholder.com/30'
            }
            onError={(e) => (e.target.src = 'https://via.placeholder.com/30')}
            alt="avatar"
            className="w-8 h-8 rounded-full object-cover mt-1 flex-shrink-0"
          />

          {/* Bubble */}
          <div
            className={`p-3 rounded-2xl shadow-sm relative ${
              isOwn
                ? 'bg-blue-600 text-white bubble-tail-right'
                : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 bubble-tail-left'
            }`}
          >
            <p className="text-xs font-medium mb-1">
              {msg.senderId?.fullName}{' '}
              {msg.senderId?.role === 'specialist' ? '(Specialist)' : '(User)'}
            </p>

            {/* Text content */}
            {msg.type === 'text' && msg.content && (
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            )}

            {/* File attachments */}
            {msg.type === 'file' && msg.attachments?.length > 0 && (
              <AttachmentPreview attachments={msg.attachments} />
            )}

            {/* Zoom meeting */}
            {msg.type === 'zoom' && msg.zoomMeeting?.joinUrl && (
              <ZoomCard zoomMeeting={msg.zoomMeeting} isOwn={isOwn} />
            )}

            {/* Timestamp */}
            <p className={`text-xs mt-1 group relative ${isOwn ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`}>
              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded p-1 -top-8 left-0 whitespace-nowrap">
                {new Date(msg.createdAt).toLocaleString()}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ── JSX ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900 p-4">

      {/* Left: participant list */}
      <div className="w-1/3 md:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-lg mr-4 overflow-y-auto">
        <div className="p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            {user?.role === 'specialist' ? 'Chat Users' : 'Chat Specialists'}
          </h2>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-600 dark:text-gray-300">Loading…</div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 dark:text-red-400">Error: {error}</div>
        ) : participants.length === 0 ? (
          <div className="p-4 text-center text-gray-600 dark:text-gray-300">
            No {user?.role === 'specialist' ? 'users' : 'specialists'} found.
          </div>
        ) : (
          <ul className="divide-y dark:divide-gray-700">
            {participants.map((p) => (
              <li
                key={p.consultationId}
                className={`p-4 flex items-start space-x-3 cursor-pointer transition-colors duration-200 ${
                  selectedParticipant?.consultationId === p.consultationId
                    ? 'bg-blue-100 dark:bg-blue-900/70 border-l-4 border-blue-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => handleParticipantSelect(p)}
              >
                <img
                  src={p.profilePicture ? `${BASE_URL}${p.profilePicture}` : 'https://via.placeholder.com/40'}
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/40')}
                  alt={p.fullName}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {p.fullName} {p.role === 'specialist' ? '(Specialist)' : '(User)'}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatLastMessageTime(p.lastMessageTime)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {p.lastMessage || 'No messages yet'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right: chat panel */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg flex flex-col">
        {selectedParticipant ? (
          chatLoading ? (
            <div className="flex-1 flex items-center justify-center text-gray-600 dark:text-gray-300">
              <svg className="animate-spin h-8 w-8 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Loading messages…
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-red-600 dark:text-red-400">Error: {error}</div>
          ) : (
            <>
              {/* Chat header */}
              <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img
                      src={selectedParticipant.profilePicture ? `${BASE_URL}${selectedParticipant.profilePicture}` : 'https://via.placeholder.com/40'}
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/40')}
                      alt={selectedParticipant.fullName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                    />
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      {selectedParticipant.fullName}{' '}
                      {selectedParticipant.role === 'specialist' ? '(Specialist)' : '(User)'}
                    </h2>
                  </div>

                  {/* Zoom button in header */}
                  <button
                    onClick={createZoomMeeting}
                    disabled={creatingZoom}
                    title="Start Zoom meeting"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl text-sm font-semibold transition"
                  >
                    <FaVideo size={14} />
                    {creatingZoom ? 'Creating…' : 'Start Zoom'}
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900"
                style={{ maxHeight: 'calc(100vh - 220px)' }}
              >
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 mt-10">
                    No messages yet. Say hello!
                  </div>
                ) : (
                  messages.map(renderMessage)
                )}
              </div>

              {/* File preview strip */}
              <FilePickerPreview files={pendingFiles} onRemove={removePendingFile} />

              {/* Input bar */}
              <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center space-x-2">

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    className="hidden"
                    onChange={handleFileChange}
                  />

                  {/* Paperclip */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach files"
                    className="p-3 rounded-full text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                  >
                    <FaPaperclip size={18} />
                  </button>

                  {/* Text input */}
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={uploading}
                    className="flex-1 p-3 rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={pendingFiles.length ? `${pendingFiles.length} file(s) ready — press Send` : 'Type a message…'}
                  />

                  {/* Send */}
                  <button
                    onClick={pendingFiles.length ? sendFiles : sendMessage}
                    disabled={uploading || (!messageInput.trim() && !pendingFiles.length)}
                    className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    title={pendingFiles.length ? 'Send files' : 'Send message'}
                  >
                    {uploading ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    ) : (
                      <FaPaperPlane size={18} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-600 dark:text-gray-300">
            Select a {user?.role === 'specialist' ? 'user' : 'specialist'} to start chatting
          </div>
        )}
      </div>

      {/* Bubble tail CSS */}
      <style>{`
        .bubble-tail-left::before {
          content: '';
          position: absolute;
          left: -8px; top: 10px;
          border: 8px solid transparent;
          border-right-color: #ffffff;
        }
        .bubble-tail-right::before {
          content: '';
          position: absolute;
          right: -8px; top: 10px;
          border: 8px solid transparent;
          border-left-color: #2563eb;
        }
        .dark .bubble-tail-left::before { border-right-color: #374151; }
      `}</style>
    </div>
  );
}

export default Chat;
